/* eslint-disable no-undef */
import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { Row, Col } from 'antd';

import Layout from 'components/layout';
import { FilterHeader } from 'components/filter';
import { sorts } from 'utils/filters';
import { FilterPanel } from 'components/filterPanel';
import PaginatedGrid from 'components/paginatedGrid';
import ProductsGrid from 'components/productsGrid';
import Paginator, { getSeoRelLinks } from 'components/paginator';
import { baseTranslations, getCountryFromMarket } from 'utils/translations';
import { useErrorContext } from 'context/error';
import { useUser } from 'context/user';
import { getGridProps, getProducts } from 'actions/products';
import { getSeo } from 'actions/general';
import { filterTransformer, DEFAULT_LIMIT, getMarket, getRealID, userAgentIsMobile, COOKIES, getCookie } from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { TIER_POSITIONS } from 'utils/constants/cms';
import { AUTO_BONDS } from 'utils/constants/cookies';
import { useI18n } from 'context/i18n';
import { createPagesArray, getPageProps, updateUrlWithFilters } from 'utils/grid';
import { useGridVouchers } from 'hooks/useGridVouchers';
import { useGrid } from 'hooks/useGrid';
import { checkFilters, parseProductsEcommerceGA4 } from 'utils/gtmUtils';
import { dataLayerHandleEvent } from 'utils/dataLayers';

import styles from 'styles/ProductList.module.css';

const TierComponent =
  IS_CAMPER ?
    dynamic(() => import('@camper/react-web-components').then((res) => res.ORGANISMS.TierComponent))
  : dynamic(() => import('@nnormal/react-web-components').then((res) => res.ORGANISMS.TierBannerComponent));

const SeoContent = dynamic(() => import('components/seo-content').then((mod) => mod.SeoContent));

const DEFAULT_SORT = Object.keys(sorts)[0];

function WithLayout({ defaultProps, children, breadcrumbI18n, camperlab, recycled, isApp, breadcrumbsBottom, metatags }) {
  if (isApp) {
    return children;
  }

  return (
    <Layout
      {...defaultProps}
      breadcrumbI18n={breadcrumbI18n}
      breadcrumbs
      breadcrumbsBottom={breadcrumbsBottom}
      scrollArrow={true}
      camperlab={camperlab}
      recycled={recycled}
    >
      {children}
    </Layout>
  );
}

WithLayout.propTypes = {
  children: PropTypes.node,
  isApp: PropTypes.bool,
  footerProps: PropTypes.any,
  tnmProps: PropTypes.any,
  breadcrumbI18n: PropTypes.object,
  ribbon: PropTypes.any,
  camperlab: PropTypes.bool,
  recycled: PropTypes.bool,
  tiersProps: PropTypes.object,
  title: PropTypes.string,
  breadcrumbsBottom: PropTypes.bool,
};

export default function ProductsListBrand({
  defaultProps,
  firstProducts,
  previousPageProducts,
  nextPageProducts,
  total,
  availableFilters,
  baseAvailable,
  locale,
  initialPage,
  appliedFilters,
  filtersCount,
  tiersProps,
  breadcrumbI18n,
  gtmProps,
  currentList,
  camperlab,
  title,
  description,
  metatags,
  autoappliedVoucher,
  urlCanonical,
  hasFiltersInURL,
  originalCta,
  seoContent,
  recycled,
  subgridProducts,
  subgridUrlCollection,
  isApp,
}) {
  const { spreadError } = useErrorContext();
  const { setContextFilters } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const [productsPerPage, setProductsPerPage] = useState(() =>
    createPagesArray({
      initialPage,
      firstProducts,
      previousPageProducts,
      nextPageProducts,
    }),
  );
  const { voucherApplied } = useGridVouchers({ firstProducts, originalCta, autoappliedVoucher });

  // Filters
  const [currentFilter, setCurrentFilter] = useState(appliedFilters);
  const [currentAvailableFilters, setCurrentAvailableFilters] = useState(availableFilters || {});
  const [currentSort, setCurrentSort] = useState(DEFAULT_SORT);
  const [currentTotal, setCurrentTotal] = useState(total);
  const [currentFiltersCount, setCurrentFiltersCount] = useState(filtersCount);
  const [hasFiltersUrl, setHasFiltersUrl] = useState(hasFiltersInURL);
  const [mobileFilterPanelVisible, setMobileFilterPanelVisible] = useState(false);
  const [filterPanelVisible, setFilterPanelVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const initialCamperlabFilters = useRef(null);
  const { pagesRange, tiersIndexes } = useGrid({ tiersProps, productsPerPage });

  const { query } = router;
  const { target, product } = query;

  const market = getMarket(locale);
  const country = getCountryFromMarket(market);
  const typologyFilterValues = currentFilter?.typologies?.map((item) => item.value) ?? [];
  const hasStandalone = tiersProps[TIER_POSITIONS.STANDALONE] !== null && tiersProps[TIER_POSITIONS.STANDALONE] !== undefined;
  const hasTitleDescription = title !== null && title !== undefined;
  const totalPages = Math.ceil(total / DEFAULT_LIMIT);
  const isGridSeeAll = currentFiltersCount === 0 && !hasFiltersUrl;
  const seoLinks = getSeoRelLinks(initialPage, totalPages, urlCanonical, isGridSeeAll);
  const filterPanelClassname = subgridProducts.length === 0 ? styles.filterPanelCol : styles.filterPanelColStatic;

  useEffect(() => {
    setContextFilters(currentFilter);
  }, [currentFilter]);

  const handleGetProducts = (page, filters, sort, limit, callback, sendGTM = true) => {
    if (page < 0) return;
    const newFiltersCount =
      Object.keys(filters).length > 0 ?
        Object.keys(filters)
          .filter((item) => item !== 'first')
          .reduce((a, b) => filters[b].length + a, 0)
      : 0;
    const queryFilters = filterTransformer.toQuery(filters);
    const { key, value } = checkFilters(filters);

    if (sendGTM && key.length > 0 && value.length > 0 && currentSort === sort) {
      dataLayerHandleEvent({
        event: 'eventGA',
        eventCat: 'Apply Filter',
        eventAct: [target, ...key],
        eventLbl: [...value],
      });
    }

    const autoVoucher = getCookie(COOKIES.AUTO_BONDS) || '';
    const voucher = getCookie(COOKIES.BONDS) || '';
    const voucherDiscount = IS_CAMPER && autoVoucher && autoVoucher === AUTO_BONDS.APPLY;
    // in getProducts page is not the page but the offset of the list
    const pageOffset = page * DEFAULT_LIMIT;
    getProducts({
      lang: locale,
      target,
      page: pageOffset,
      filters: queryFilters,
      limit,
      sort,
      product,
      voucherDiscount: voucherDiscount ? voucher : undefined,
    })
      .then(({ products: responseProducts, total: responseTotal, availableFilters: newAvailableFilters }) => {
        if (responseTotal === 0) {
          dataLayerHandleEvent({
            event: 'eventGA',
            eventCat: 'Apply Filter',
            eventAct: 'emptyGrid',
            eventLbl: gtmProps?.currentList,
          });
        }
        if (sendGTM) {
          const eventLayerGA4 = {
            event: 'view_item_list',
            nonInteraction: '1',
            ecommerce: {
              currency: responseProducts.length ? responseProducts[0].prices?.currency : '',
              items: parseProductsEcommerceGA4(responseProducts, gtmProps?.currentListId, gtmProps?.currentListName ?? gtmProps?.currentList, page),
            },
          };
          dataLayerHandleEvent(eventLayerGA4);
        }
        setCurrentFiltersCount(newFiltersCount);
        const url = window.location.href;
        const hasFilterInUrl = url.includes('?filter.') || url.includes('&filter.');
        setHasFiltersUrl(hasFilterInUrl);
        setCurrentFilter(filters);
        setCurrentAvailableFilters(newAvailableFilters);
        setCurrentSort(sort);
        return callback(responseProducts, responseTotal);
      })
      .catch(spreadError);
  };

  const handleLoadPage = (newPageIndex, triggerPosition) => {
    if (newPageIndex < 0 || newPageIndex > totalPages - 1) return;

    /**
     * Para evitar fetches duplicados, tenemos que retornar con diferentes condiciones dependiendo de donde viene el evento de carga
     * Si el trigger es el de en medio siempre miramos si la pagina anterior tiene productos.
     * Si el trigger es el de abajo, miramos si existe el array en el index de la pagina. Si existe significa que el trigger anterior ya ha cargado o esta cargando los productos.
     */
    if (triggerPosition === 'middle' && productsPerPage[newPageIndex] && productsPerPage[newPageIndex].length) {
      return;
    }
    if (triggerPosition === 'bottom' && productsPerPage[newPageIndex] && productsPerPage[newPageIndex].length) {
      return;
    }

    // si no existe el array en el index de la pagina añadimos un array vacio
    if (!productsPerPage[newPageIndex]) {
      setProductsPerPage((prev) => {
        const prevCopy = [...prev];
        prevCopy[newPageIndex] = [];
        return prevCopy;
      });
    }
    handleGetProducts(
      newPageIndex,
      currentFilter,
      currentSort,
      DEFAULT_LIMIT,
      (response) => {
        setProductsPerPage((prev) => {
          const prevCopy = [...prev];
          prevCopy[newPageIndex] = response;
          return prevCopy;
        });
      },
      true,
    );
  };

  const handleApplyFilters = (filters, sort = DEFAULT_SORT) => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    setProductsPerPage([[]]);

    const filtersToUse =
      camperlab && initialCamperlabFilters.current !== null ?
        {
          ...JSON.parse(JSON.stringify(filters)),
          ...JSON.parse(JSON.stringify(initialCamperlabFilters.current)),
        }
      : filters;

    const queryFilters = filterTransformer.toQuery(filtersToUse);
    handleGetProducts(0, filtersToUse, sort, DEFAULT_LIMIT, (response, newTotal) => {
      setProductsPerPage([response]);
      updateUrlWithFilters(queryFilters, sort);
      setCurrentTotal(newTotal);
    });
  };

  const handleOnClickFilterBtn = (visible, fakeApply = false) => {
    setFilterPanelVisible(visible);
    // si ya ha cargado los valores filtrados hacemos un loading falso
    if (fakeApply && productsPerPage.length === 1 && productsPerPage[0].length !== 0) {
      const copy = JSON.parse(JSON.stringify(productsPerPage));
      setProductsPerPage([[]]);
      setTimeout(() => setProductsPerPage(copy), 500);
    }
  };

  const handleMobileOnClickFilterBtn = (visible, fakeApply = false) => {
    document.querySelector('body').classList.toggle('filterOpen');
    setMobileFilterPanelVisible(visible);
    if (fakeApply && productsPerPage.length === 1 && productsPerPage[0].length !== 0) {
      const copy = JSON.parse(JSON.stringify(productsPerPage));
      setProductsPerPage([[]]);
      setTimeout(() => setProductsPerPage(copy), 500);
    }
  };

  useEffect(() => {
    if (camperlab) {
      initialCamperlabFilters.current = JSON.parse(JSON.stringify(appliedFilters));
    }
  }, []);

  // Funciones de filtros
  const handleClickSort = (sortValue) => {
    dataLayerHandleEvent({
      event: 'eventGA',
      eventCat: 'ecommerce',
      eventAct: 'gridSort',
      eventLbl: sortValue,
    });
    handleApplyFilters(appliedFilters, sortValue);
  };

  const handleRemoveFilter = (id, option) => {
    const currentFilters = { ...currentFilter };
    let indexOfFilter = null;

    currentFilters[id]?.forEach((item, index) => {
      const { key } = option;
      if (key === item.key) {
        indexOfFilter = index;
      }
    });

    if (indexOfFilter === null) {
      throw new Error('FATAL: Index for added filter not found.');
    }

    currentFilters[id].splice(indexOfFilter, 1);
    if (currentFilters[id].length === 0) {
      delete currentFilters[id];
    }
    if (Object.keys(currentFilters).length === 1 && Object.keys(currentFilters)[0] === 'first') {
      delete currentFilters.first;
    }

    handleApplyFilters(currentFilters, currentSort);
  };

  return (
    <WithLayout
      defaultProps={{
        ...defaultProps,
        metatags,
        pageName: 'grid',
        extratags: [seoLinks],
      }}
      isApp={isApp}
      breadcrumbs
      breadcrumbI18n={breadcrumbI18n}
      breadcrumbsBottom={hasStandalone || hasTitleDescription}
      camperlab={camperlab}
      recycled={recycled}
      scrollArrow={true}
    >
      <div>
        <Row>
          <Col span={24} className={styles.productListTitle}>
            {tiersProps[TIER_POSITIONS.STANDALONE] ?
              <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE]} typology={typologyFilterValues} />
            : <>
                <h1 className={styles.title} dangerouslySetInnerHTML={{ __html: title }} />
                <h2 className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
              </>
            }
          </Col>
          {IS_CAMPER ?
            <>
              {/* Mobile components */}
              <Col span={24} className={styles.filterHeaderCol} id="mobile-plp-filter-nav" data-mobile>
                <FilterHeader
                  open={mobileFilterPanelVisible}
                  toggle={() => handleMobileOnClickFilterBtn(!mobileFilterPanelVisible)}
                  count={currentFiltersCount}
                  filters={appliedFilters}
                  onRemoveFilter={handleRemoveFilter}
                  onReset={() => handleApplyFilters({}, currentSort)}
                  total={currentTotal}
                  onSort={handleClickSort}
                  currentSort={currentSort}
                  hiddenFilters={['first']}
                />
              </Col>
              <Col
                xs={{ span: mobileFilterPanelVisible ? 24 : 0 }}
                md={{ span: mobileFilterPanelVisible ? 8 : 0 }}
                lg={{ span: mobileFilterPanelVisible ? 8 : 0 }}
                xl={{ span: mobileFilterPanelVisible ? 8 : 0 }}
                xxl={{ span: mobileFilterPanelVisible ? 6 : 0 }}
                className={filterPanelClassname}
                id="plp-filter-panel"
                data-open={mobileFilterPanelVisible}
                data-mobile
              >
                <FilterPanel
                  appliedFilters={currentFilter}
                  availableFilters={currentAvailableFilters}
                  currentSort={currentSort}
                  onApply={handleApplyFilters}
                  total={currentTotal}
                  onClickCloseBtn={handleMobileOnClickFilterBtn}
                  baseAvailable={baseAvailable}
                  filtersCount={currentFiltersCount}
                  country={country}
                  camperlab={camperlab}
                />
              </Col>
              {/* desktop components */}
              <Col span={24} className={styles.filterHeaderCol} id="plp-filter-nav" data-desktop>
                <FilterHeader
                  open={filterPanelVisible}
                  toggle={() => handleOnClickFilterBtn(!filterPanelVisible)}
                  count={currentFiltersCount}
                  filters={currentFilter}
                  onRemoveFilter={handleRemoveFilter}
                  onReset={() => handleApplyFilters({}, currentSort)}
                  total={currentTotal}
                  onSort={handleClickSort}
                  currentSort={currentSort}
                  hiddenFilters={['first']}
                />
              </Col>
              <Col
                xs={{ span: filterPanelVisible ? 24 : 0 }}
                md={{ span: filterPanelVisible ? 8 : 0 }}
                lg={{ span: filterPanelVisible ? 8 : 0 }}
                xl={{ span: filterPanelVisible ? 8 : 0 }}
                xxl={{ span: filterPanelVisible ? 6 : 0 }}
                className={filterPanelClassname}
                id="plp-filter-panel"
                data-open={filterPanelVisible}
              >
                <FilterPanel
                  appliedFilters={currentFilter}
                  availableFilters={currentAvailableFilters}
                  currentSort={currentSort}
                  onApply={handleApplyFilters}
                  total={currentTotal}
                  onClickCloseBtn={handleOnClickFilterBtn}
                  baseAvailable={baseAvailable}
                  filtersCount={currentFiltersCount}
                  country={country}
                  camperlab={camperlab}
                />
              </Col>
            </>
          : null}
          <Col
            className={styles.gridColWrapper}
            xs={{ span: 24 }}
            md={{ span: filterPanelVisible ? 16 : 24 }}
            lg={{ span: filterPanelVisible ? 16 : 24 }}
            xl={{ span: filterPanelVisible ? 16 : 24 }}
            xxl={{ span: filterPanelVisible ? 18 : 24 }}
          >
            {IS_NNORMAL ?
              <h1 className={styles.gridTitle}>
                {t('listado.productos', `grid.title.${breadcrumbI18n.target}.${breadcrumbI18n.family}`, 'Todos los zapatos')}
              </h1>
            : null}
            <Row className={`${styles.rowGrid} grid`}>
              {currentTotal > 0 &&
                productsPerPage.map((products, idx) => {
                  const pageProps = getPageProps({
                    products,
                    index: idx,
                    pagesRange,
                    tiersIndexes,
                    tiersProps,
                    TierComponent,
                  });
                  return (
                    <PaginatedGrid
                      key={`page-${idx + 1}`}
                      page={idx}
                      totalPages={totalPages}
                      products={pageProps.products}
                      currentList={currentList}
                      currentListName={title}
                      placeholder={pageProps.placeHolderArray}
                      locale={locale}
                      colXS={{ span: 12 }}
                      colMD={{ span: filterPanelVisible ? 12 : 8 }}
                      colLG={{ span: filterPanelVisible ? 8 : 6 }}
                      colXL={{ span: filterPanelVisible ? 8 : 6 }}
                      colXXL={{ span: filterPanelVisible ? 8 : 6 }}
                      handleLoadNextPage={handleLoadPage}
                      camperlab={camperlab}
                      withLabel
                      recalculating={false}
                      voucherApplied={voucherApplied}
                      hasModal={true}
                      updatePage={(newPage) => setCurrentPage(newPage)}
                      saveLastProduct={true}
                    />
                  );
                })}
            </Row>
          </Col>
        </Row>
        <Row>
          {!isApp && tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM] && (
            <Col span={24} className={styles.productListTitle}>
              <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM]} />
            </Col>
          )}
          {IS_CAMPER && subgridProducts.length > 0 && (
            <Row
              className={`${styles.rowGrid} ${styles.rowSubgrid} grid`}
              style={{
                '--background': 'var(--color-subgrid)',
                'background-color': 'var(--color-subgrid)',
              }}
            >
              <Col span={24}>
                <div className={styles.subgridTitle} dangerouslySetInnerHTML={{ __html: t('listado.productos', 'subgrid.title', 'check <i>these</i> out:') }} />
              </Col>
              <ProductsGrid
                origin="grid"
                products={subgridProducts}
                locale={locale}
                colXS={{ span: 12 }}
                colMD={{ span: 8 }}
                colLG={{ span: 6 }}
                colXL={{ span: 6 }}
                colXXL={{ span: 6 }}
                withHover
                withSKU
                withLabel={true}
                currentList="subgrid-products"
                camperlab={false}
                currentListName="subgrid-products"
                recalculating={false}
                voucherApplied={false}
                withModal={false}
              />
              <Col span={24} className={styles.discoverMoreCol}>
                <a className={styles.btnDiscoverMore} href={subgridUrlCollection}>
                  {t('listado.productos', 'subgrid.discover.more', 'Discover more')}
                </a>
              </Col>
            </Row>
          )}
          {!isApp && tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM_SEO] && <TierComponent {...tiersProps[TIER_POSITIONS.STANDALONE_BOTTOM_SEO]} />}
          {IS_CAMPER && !isApp && seoContent !== null ?
            <SeoContent content={seoContent} camperlab={camperlab} />
          : null}
        </Row>
        {totalPages > 0 && isGridSeeAll && (
          <div className={filtersCount === 0 && currentPage !== totalPages ? styles.paginatorDiv : `${styles.paginatorDiv} ${styles.hidden}`}>
            <Paginator totalPages={totalPages} currentPage={currentPage} url={urlCanonical} locale={locale} />
          </div>
        )}
      </div>
    </WithLayout>
  );
}

// ProductsListBrand.propTypes = {
//   firstProducts: PropTypes.array,
//   total: PropTypes.number,
//   availableFilters: PropTypes.object,
//   baseAvailable: PropTypes.object,
//   locale: PropTypes.string,
//   defaultLocale: PropTypes.string,
//   datetime: PropTypes.string,
//   uuid: PropTypes.string,
//   appliedFilters: PropTypes.object,
//   filtersCount: PropTypes.number,
//   hasFiltersInURL: PropTypes.bool,
//   footerProps: PropTypes.any,
//   tnmProps: PropTypes.any,
//   gtmProps: PropTypes.object,
//   tiersProps: PropTypes.object,
//   initialPage: PropTypes.number,
//   currentList: PropTypes.string,
//   ribbon: PropTypes.any,
//   camperlab: PropTypes.bool,
//   title: PropTypes.string,
//   description: PropTypes.string,
//   metatags: PropTypes.object,
//   breadcrumbI18n: PropTypes.object,
//   autoappliedVoucher: PropTypes.bool,
//   urlCanonical: PropTypes.string,
//   originalCta: PropTypes.object,
//   resolvedUrl: PropTypes.string,
//   seoContent: PropTypes.object,
//   recycled: PropTypes.bool,
//   subgridProducts: PropTypes.array,
//   subgridUrlCollection: PropTypes.string,
// };

ProductsListBrand.i18nPage = 'grid';

// import type from next GetServerSidePropsContext
/** @param {import("next").GetServerSidePropsContext} context  */
export async function getServerSideProps(context) {
  const lang = context.locale;
  const { target, product, brand, page, sort, ...rest } = context.query;
  const targets = Object.keys(baseTranslations.target)
    .map((langCode) => baseTranslations.target[langCode])
    .reduce((a, b) => a.concat(b));
  const families = Object.keys(baseTranslations.product)
    .map((langCode) => baseTranslations.product[langCode])
    .reduce((a, b) => a.concat(b));

  if (!targets.includes(target) || !families.includes(product)) {
    return {
      notFound: true,
    };
  }

  const filters = {};

  Object.entries(rest).forEach(([key, value]) => {
    if (['page', 'sort'].includes(key)) {
      filters[key] = value;
    }
    if (key.startsWith('filter.')) {
      filters[key] = value;
    }
  });

  const hasFiltersInURL = Object.keys(filters).length > 0;

  try {
    const targetRealID = getRealID('target', target);
    const familyRealID = getRealID('product', product);

    const pageNumber = Number(page);
    const pageOffset = pageNumber > 1 ? (pageNumber - 1) * DEFAULT_LIMIT : 0;
    const appliedSort = sort && sort !== DEFAULT_SORT ? sort : false;

    let gridId = null;
    try {
      const gridProps = await getGridProps({
        context,
        locale: lang,
        slug: brand,
        cache: context.query?.cache ?? undefined,
      });
      if (Array.isArray(gridProps.grid.filters)) {
        gridProps.grid.filters.forEach(({ type, value }) => {
          filters[`filter.${type}`] = value;
        });
      }
      if (gridProps.grid.id) {
        gridId = gridProps.grid.id;
      }
    } catch (error) {
      console.error('Error retrieving gridProps', JSON.stringify(error));
    }

    const previousPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang,
          target,
          page: pageOffset - DEFAULT_LIMIT,
          filters,
          sort: appliedSort,
          product,
        });
    const nextPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang,
          target,
          page: pageOffset + DEFAULT_LIMIT,
          filters,
          sort: appliedSort,
          product,
        });

    // la segunda llamada es para traer los filtros de la pagina. Porque si limitamos con filters no trae todos, solo los que aplica a los productos respondidos
    const [
      {
        products,
        total,
        availableFilters,
        currentList,
        currentListId,
        camperlab,
        title,
        description,
        urlCanonical,
        urlCanonicalGTM,
        seoContent,
        rewalk,
        relatedCollectionId,
        urlRelatedCollection,
      },
      filtersResponse,
      previousPage,
      nextPage,
      { defaultProps, tiers, cmsSeoContent },
    ] = await Promise.all([
      getProducts({
        context,
        lang,
        target,
        page: pageOffset,
        gridId,
        filters,
        sort: appliedSort,
        product,
        seoContent: true,
      }),
      getProducts({
        context,
        lang,
        target,
        product,
        page: 0,
        limit: 1,
        filters: null,
      }),
      previousPagePromise,
      nextPagePromise,
      initCMS({
        context,
        locale: lang,
        tierParams: ['collection', [brand], targetRealID, familyRealID],
        tnmSelectedTarget: targetRealID,
        seoContent: { target: targetRealID },
      }),
    ]);

    const baseAvailable = filtersResponse.availableFilters;
    const appliedFilters = Object.keys(filters).length > 0 ? filterTransformer.decode(filters, availableFilters) : {};
    const filtersCount = Object.keys(appliedFilters).length > 0 ? Object.keys(appliedFilters).reduce((a, b) => appliedFilters[b].length + a, 0) : 0;

    const gtmPage = pageNumber > 1 ? (pageNumber - 1) * DEFAULT_LIMIT : 0;
    const gtmProps = {
      target,
      targetRealID,
      product,
      familyRealID,
      products,
      currentList: currentList ?? null,
      currentListId: currentListId ?? null,
      camperlab,
      currentListName: title ?? '',
      page: gtmPage,
      url: urlCanonicalGTM ?? null,
    };

    const queryParams = [
      `profile=${lang}`,
      `family=${familyRealID || '1'}`,
      `gender=${targetRealID || 'X'}`,
      `option=${targetRealID || 'X'}`,
      'page=PRODUCTOS',
    ];
    const collectionFilter = filters['filter.collection'];
    const conceptFilter = filters['filter.concept'];
    const typologyFilter = filters['filter.typology'];
    const sustainabilityFilter = filters['filter.sustainability'];
    if (collectionFilter) {
      queryParams.push(`specialCollection=${collectionFilter}`);
    }
    if (conceptFilter) {
      queryParams.push(`collection=${conceptFilter}`);
    }
    if (typologyFilter) {
      queryParams.push(`typology=${typologyFilter}`);
    }
    if (sustainabilityFilter) {
      queryParams.push(`sustainability=${sustainabilityFilter}`);
    }
    const hasSubgrid = relatedCollectionId !== null && relatedCollectionId !== undefined && relatedCollectionId !== '';
    let subgridUrlCollection = '';
    let subgridProducts = [];
    if (hasSubgrid) {
      const resolvedUrlWithoutQuery = context.resolvedUrl.split('?')[0];
      subgridUrlCollection = urlRelatedCollection || `/${lang}${resolvedUrlWithoutQuery}?filter.collection=${relatedCollectionId}`;
      const subgridResponse = await getProducts({
        context,
        lang,
        target,
        page: 0,
        limit: 8,
        filters: { 'filter.collection': relatedCollectionId },
        sort: false,
        product,
        seoContent: true,
      });
      subgridProducts = subgridResponse.products.slice(0, 8);
    }

    // Con el nuevo cambio de tbl_grid hay que pasar el gridId a getSeo
    if (gridId) {
      queryParams.push(`gridId=${gridId}`);
    }
    const metatags = await getSeo(context, queryParams);

    const breadcrumbI18n = {
      target: targetRealID.toLocaleLowerCase() || null,
      family: familyRealID || null,
      brand: title || null,
    };

    const originalCta = {};
    if (tiers?.standalone && tiers?.standalone?.ctaText) {
      originalCta.text = tiers.standalone.ctaText;
      if (tiers.standalone.ctaTextProps) {
        originalCta.fontColor = tiers.standalone.ctaTextProps.fontColor || tiers?.textProps?.fontColor || '#000000';
        tiers.standalone.ctaTextProps.fontColor = '#ffffff00';
      }
    }
    const isApp = context?.req?.cookies[COOKIES.APP_IPAD] === 'true' || false;

    return {
      props: {
        defaultProps,
        currentList: currentList || null,
        firstProducts: products,
        previousPageProducts: previousPage?.products ?? null,
        nextPageProducts: nextPage?.products?.length > 0 ? nextPage.products : null,
        total,
        availableFilters,
        baseAvailable,
        appliedFilters,
        filtersCount,
        hasFiltersInURL,
        locale: lang,
        defaultLocale: lang,
        gtmProps,
        tiersProps: !isApp && tiers ? tiers : {},
        initialPage: pageNumber || 1,
        camperlab,
        title: title ?? null,
        description: description ?? null,
        metatags,
        breadcrumbI18n,
        urlCanonical,
        originalCta,
        resolvedUrl: context.resolvedUrl,
        recycled: rewalk ?? false,
        seoContent:
          seoContent ?
            {
              ...seoContent,
              cms:
                cmsSeoContent ?
                  {
                    ...cmsSeoContent,
                    gridName: currentList ?? title ?? '',
                  }
                : null,
            }
          : null,
        subgridProducts,
        subgridUrlCollection,
        isApp,
      },
    };
  } catch (e) {
    console.error(e);
    context.res.statusCode = 500;
    return {
      props: {
        error: `${context.resolvedUrl} SSR Error: ${e}`,
        permanent: false,
      },
    };
  }
}
